// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Pure conversation state machine. Kept free of React and timers so it can be
// unit-tested directly (see elia-chat.test.ts). The provider owns the async
// timing and generates message ids; this reducer only transforms state.

import type { EliaChatHistoryItem, EliaMessage } from './elia-chat-types';
import { createInitialThread } from './elia-chat-fixtures';

export type EliaThreadState = {
  messages: EliaMessage[];
  status: 'idle' | 'sending';
  /** The query awaiting retry after a failure, or null. */
  failedQuery: string | null;
};

export type EliaThreadAction =
  | { type: 'SEND_START'; userMessage: EliaMessage }
  | { type: 'SEND_SUCCESS'; assistantMessage: EliaMessage }
  | { type: 'SEND_FAILURE'; failureMessage: EliaMessage; query: string }
  | { type: 'RETRY_START' }
  | { type: 'RESET'; messages?: EliaMessage[] };

export function createInitialThreadState(): EliaThreadState {
  return { messages: createInitialThread(), status: 'idle', failedQuery: null };
}

/** Whitespace-only text is not sendable. */
export function isSendableText(text: string): boolean {
  return text.trim().length > 0;
}

export function isBusy(state: EliaThreadState): boolean {
  return state.status === 'sending';
}

/** Recent, successfully-exchanged turns to send as context to the client. */
export function buildHistory(messages: EliaMessage[], limit = 12): EliaChatHistoryItem[] {
  return messages
    .filter((m) => m.status !== 'failed' && m.status !== 'pending')
    .map((m) => ({ role: m.role, content: m.content }))
    .slice(-limit);
}

// Once the conversation advances, drop the starter/follow-up suggestion sets so
// stale chips don't linger (FR-10).
function clearSuggestions(messages: EliaMessage[]): EliaMessage[] {
  return messages.map((m) => (m.suggestions ? { ...m, suggestions: undefined } : m));
}

// Retry removes the trailing failure bubble(s) but keeps the user's question.
function dropTrailingFailures(messages: EliaMessage[]): EliaMessage[] {
  const out = [...messages];
  while (out.length > 0 && out[out.length - 1].status === 'failed') {
    out.pop();
  }
  return out;
}

export function eliaThreadReducer(
  state: EliaThreadState,
  action: EliaThreadAction,
): EliaThreadState {
  switch (action.type) {
    case 'SEND_START': {
      // Block duplicate submissions while a response is pending (FR-16).
      if (state.status === 'sending') return state;
      return {
        messages: [...clearSuggestions(state.messages), action.userMessage],
        status: 'sending',
        failedQuery: null,
      };
    }
    case 'RETRY_START': {
      if (state.status === 'sending') return state;
      return {
        messages: dropTrailingFailures(state.messages),
        status: 'sending',
        failedQuery: null,
      };
    }
    case 'SEND_SUCCESS': {
      return {
        messages: [...state.messages, action.assistantMessage],
        status: 'idle',
        failedQuery: null,
      };
    }
    case 'SEND_FAILURE': {
      return {
        messages: [...state.messages, action.failureMessage],
        status: 'idle',
        failedQuery: action.query,
      };
    }
    case 'RESET': {
      return {
        messages: action.messages ?? createInitialThread(),
        status: 'idle',
        failedQuery: null,
      };
    }
    default:
      return state;
  }
}
